import { DateTime } from 'luxon';

import type { CalendarEvent } from '../types';

/**
 * Generate mock events for testing the calendar view.
 * Events are generated relative to the current week to ensure visibility.
 */
function generateMockEvents(): CalendarEvent[] {
  const now = DateTime.local();
  const weekStart = now.startOf('week');

  const events: Omit<CalendarEvent, 'externalId' | 'worklogId' | 'myResponseStatus'>[] = [
    // Regular events of different durations
    {
      id: 'event-1',
      calendarId: 'cal-1',
      summary: 'Daily standup',
      description: 'Team sync meeting to discuss progress and blockers',
      startsAt: weekStart.plus({ days: 0, hours: 10 }).toISO()!,
      endsAt: weekStart.plus({ days: 0, hours: 10, minutes: 30 }).toISO()!,
      isAllDay: false,
      location: 'Zoom',
      attendees: [],
    },
    {
      id: 'event-2',
      calendarId: 'cal-1',
      summary: 'Sprint planning',
      description: 'Planning session for the upcoming sprint',
      startsAt: weekStart.plus({ days: 0, hours: 14 }).toISO()!,
      endsAt: weekStart.plus({ days: 0, hours: 16 }).toISO()!,
      isAllDay: false,
      location: 'Conference Room A',
      attendees: [],
    },
    {
      id: 'event-3',
      calendarId: 'cal-2',
      summary: 'Code review session',
      description: null,
      startsAt: weekStart.plus({ days: 1, hours: 11 }).toISO()!,
      endsAt: weekStart.plus({ days: 1, hours: 12 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // Overlapping events (2 events at the same time)
    {
      id: 'event-4',
      calendarId: 'cal-1',
      summary: 'Team sync',
      description: 'Weekly team synchronization',
      startsAt: weekStart.plus({ days: 2, hours: 9 }).toISO()!,
      endsAt: weekStart.plus({ days: 2, hours: 10 }).toISO()!,
      isAllDay: false,
      location: 'Zoom',
      attendees: [],
    },
    {
      id: 'event-5',
      calendarId: 'cal-2',
      summary: 'Client call',
      description: 'Important client discussion about project timeline',
      startsAt: weekStart.plus({ days: 2, hours: 9, minutes: 30 }).toISO()!,
      endsAt: weekStart.plus({ days: 2, hours: 10, minutes: 30 }).toISO()!,
      isAllDay: false,
      location: 'Google Meet',
      attendees: [],
    },

    // 3-4 overlapping events
    {
      id: 'event-6',
      calendarId: 'cal-1',
      summary: 'Meeting A',
      description: 'First concurrent meeting',
      startsAt: weekStart.plus({ days: 3, hours: 14 }).toISO()!,
      endsAt: weekStart.plus({ days: 3, hours: 15 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-7',
      calendarId: 'cal-2',
      summary: 'Meeting B',
      description: 'Second concurrent meeting',
      startsAt: weekStart.plus({ days: 3, hours: 14, minutes: 15 }).toISO()!,
      endsAt: weekStart.plus({ days: 3, hours: 15, minutes: 15 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-8',
      calendarId: 'cal-3',
      summary: 'Meeting C',
      description: 'Third concurrent meeting',
      startsAt: weekStart.plus({ days: 3, hours: 14, minutes: 30 }).toISO()!,
      endsAt: weekStart.plus({ days: 3, hours: 15, minutes: 30 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-9',
      calendarId: 'cal-1',
      summary: 'Meeting D',
      description: 'Fourth concurrent meeting',
      startsAt: weekStart.plus({ days: 3, hours: 14, minutes: 45 }).toISO()!,
      endsAt: weekStart.plus({ days: 3, hours: 15, minutes: 45 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // All-day events
    {
      id: 'event-10',
      calendarId: 'cal-1',
      summary: 'Company holiday',
      description: 'Office closed',
      startsAt: weekStart.plus({ days: 4 }).toISODate()!,
      endsAt: weekStart.plus({ days: 4 }).toISODate()!,
      isAllDay: true,
      location: null,
      attendees: [],
    },
    {
      id: 'event-11',
      calendarId: 'cal-2',
      summary: 'Team offsite',
      description: 'Team building event',
      startsAt: weekStart.plus({ days: 4 }).toISODate()!,
      endsAt: weekStart.plus({ days: 5 }).toISODate()!,
      isAllDay: true,
      location: 'Mountain Resort',
      attendees: [],
    },
    {
      id: 'event-12',
      calendarId: 'cal-3',
      summary: 'Conference',
      description: null,
      startsAt: weekStart.plus({ days: 4 }).toISODate()!,
      endsAt: weekStart.plus({ days: 4 }).toISODate()!,
      isAllDay: true,
      location: 'Convention Center',
      attendees: [],
    },

    // Event crossing midnight
    {
      id: 'event-13',
      calendarId: 'cal-2',
      summary: 'Late night deployment',
      description: 'Production release window',
      startsAt: weekStart.plus({ days: 5, hours: 23 }).toISO()!,
      endsAt: weekStart.plus({ days: 6, hours: 1 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // Short event (< 15 minutes)
    {
      id: 'event-14',
      calendarId: 'cal-1',
      summary: 'Quick sync',
      description: 'Brief check-in',
      startsAt: weekStart.plus({ days: 1, hours: 15 }).toISO()!,
      endsAt: weekStart.plus({ days: 1, hours: 15, minutes: 10 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // Long event title (for ellipsis testing)
    {
      id: 'event-15',
      calendarId: 'cal-3',
      summary:
        'Very long meeting title that should be truncated with ellipsis when displayed in the calendar view',
      description:
        'This meeting has an extremely long title to test how the UI handles text overflow and ellipsis truncation',
      startsAt: weekStart.plus({ days: 2, hours: 16 }).toISO()!,
      endsAt: weekStart.plus({ days: 2, hours: 17 }).toISO()!,
      isAllDay: false,
      location: 'Room 101',
      attendees: [],
    },

    // ============================================
    // COLOR DEMO: 3 events side-by-side at same time
    // Shows blue, red, green with hatching pattern
    // ============================================
    {
      id: 'event-16',
      calendarId: 'cal-1',
      summary: '🔵 Work (синий)',
      description: 'Демонстрация цвета календаря Work - синий #4285F4',
      startsAt: weekStart.plus({ days: 5, hours: 10 }).toISO()!,
      endsAt: weekStart.plus({ days: 5, hours: 12 }).toISO()!, // 2 hours for visibility
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-17',
      calendarId: 'cal-2',
      summary: '🔴 Personal (красный)',
      description: 'Демонстрация цвета календаря Personal - красный #EA4335',
      startsAt: weekStart.plus({ days: 5, hours: 10 }).toISO()!, // Same time - overlapping!
      endsAt: weekStart.plus({ days: 5, hours: 12 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-18',
      calendarId: 'cal-3',
      summary: '🟢 Meetings (зелёный)',
      description: 'Демонстрация цвета календаря Meetings - зелёный #34A853',
      startsAt: weekStart.plus({ days: 5, hours: 10 }).toISO()!, // Same time - overlapping!
      endsAt: weekStart.plus({ days: 5, hours: 12 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // Sequential events to show colors without overlap
    {
      id: 'event-16b',
      calendarId: 'cal-1',
      summary: 'Work event',
      description: null,
      startsAt: weekStart.plus({ days: 5, hours: 13 }).toISO()!,
      endsAt: weekStart.plus({ days: 5, hours: 14 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-17b',
      calendarId: 'cal-2',
      summary: 'Personal event',
      description: null,
      startsAt: weekStart.plus({ days: 5, hours: 14 }).toISO()!,
      endsAt: weekStart.plus({ days: 5, hours: 15 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },
    {
      id: 'event-18b',
      calendarId: 'cal-3',
      summary: 'Meetings event',
      description: null,
      startsAt: weekStart.plus({ days: 5, hours: 15 }).toISO()!,
      endsAt: weekStart.plus({ days: 5, hours: 16 }).toISO()!,
      isAllDay: false,
      location: null,
      attendees: [],
    },

    // Event with all fields populated
    {
      id: 'event-19',
      calendarId: 'cal-1',
      summary: 'Complete event',
      description: 'This event has all fields filled in for comprehensive testing',
      startsAt: weekStart.plus({ days: 6, hours: 14 }).toISO()!,
      endsAt: weekStart.plus({ days: 6, hours: 16 }).toISO()!,
      isAllDay: false,
      location: '123 Main Street, Building A, Floor 3, Conference Room Beta',
      attendees: [],
    },

    // Multiple all-day events on same day
    {
      id: 'event-20',
      calendarId: 'cal-1',
      summary: 'All-day 1',
      description: null,
      startsAt: weekStart.plus({ days: 6 }).toISODate()!,
      endsAt: weekStart.plus({ days: 6 }).toISODate()!,
      isAllDay: true,
      location: null,
      attendees: [],
    },
    {
      id: 'event-21',
      calendarId: 'cal-2',
      summary: 'All-day 2',
      description: null,
      startsAt: weekStart.plus({ days: 6 }).toISODate()!,
      endsAt: weekStart.plus({ days: 6 }).toISODate()!,
      isAllDay: true,
      location: null,
      attendees: [],
    },
    {
      id: 'event-22',
      calendarId: 'cal-3',
      summary: 'All-day 3',
      description: null,
      startsAt: weekStart.plus({ days: 6 }).toISODate()!,
      endsAt: weekStart.plus({ days: 6 }).toISODate()!,
      isAllDay: true,
      location: null,
      attendees: [],
    },
  ];

  return events.map((event) => ({
    ...event,
    externalId: `mock-uid-${event.id}`,
    recurrenceId: null,
    worklogId: null,
    myResponseStatus: 'ACCEPTED' as string | null,
  }));
}

/**
 * Mock calendar data matching the CalDavCalendar structure.
 */
export const mockCalendars = [
  {
    id: 'cal-1',
    externalId: 'https://calendar.example.com/cal1',
    name: 'Work',
    color: '#4285F4', // Blue
    isSelected: true,
    syncStatus: 'synced' as const,
  },
  {
    id: 'cal-2',
    externalId: 'https://calendar.example.com/cal2',
    name: 'Personal',
    color: '#EA4335', // Red
    isSelected: true,
    syncStatus: 'synced' as const,
  },
  {
    id: 'cal-3',
    externalId: 'https://calendar.example.com/cal3',
    name: 'Meetings',
    color: '#34A853', // Green
    isSelected: true,
    syncStatus: 'synced' as const,
  },
];

export const mockEvents = generateMockEvents();
